/**
 * DASHBOARD-MESSAGE.WIDGET
 * Display message
 */

import { withTranslation, WithTranslation } from "react-i18next"
import { connect } from "react-redux"
import Highlighter from "react-highlight-words"
import { useState } from "react"
import { Message, MessageState } from "@/redux/message.types"
import { store } from "@/index"
import { STATUS_SAVED, STATUS_SAVE_ERROR, STATUS_SAVING } from "@/redux/_status.types"
import { Question } from "@/redux/question.types"
import ListButton from "@/components/list-button"
import { faTrash, faUserCircle } from "@fortawesome/free-solid-svg-icons"
import Space from "@/components/space"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import ModalConfirm from "@/components/modal-confirm"
import { messageDestroy, messageStatus } from "@/redux/message.actions"
import { SurveyState } from "@/redux/survey.types"
import { Topic } from "@/redux/topic.types"
import { topicActivate, topicEditQuestion } from "@/redux/topic.actions"
import ColorPickerWidget from "@/widgets/color-picker.widget"
import { Session } from "@/redux/_session.types"
import { Tag } from "@/redux/account.types"
import Tooltip from "@/components/tooltip"
import { t } from "i18next"

const REDACT_MARKER = "[ANONYMISÉ]"

function renderWithRedactions(text?: string | null, searchWords?: string[]) {
  const safeText = text ?? ""
  const parts = safeText.split(REDACT_MARKER)
  return (
    <>
      {parts.map((part, i) => (
        <span key={i}>
          {searchWords?.length ? (
            <Highlighter searchWords={searchWords} autoEscape={true} textToHighlight={part} />
          ) : (
            part
          )}
          {i < parts.length - 1 && (
            <Tooltip text={t("message_anonymized")} position="top">
              <span
                className="inline-block align-middle rounded-sm mx-0.5 grey cursor-help"
                style={{ width: "5rem", height: "0.85em" }}
              />
            </Tooltip>
          )}
        </span>
      ))}
    </>
  )
}

interface StateProps extends WithTranslation {
  _session: Session
  message: MessageState
  survey: SurveyState
}

interface OwnProps {
  currentQuestion: Question
  currentMessage: Message
  currentTopic: Topic
  search: string
  id?: string
  onSetColor: Function
}

type Props = StateProps & OwnProps

const MODAL_DELETE = "MODAL_DELETE"

function DashboardQuestionOpenWidget(props: Props) {
  const { t } = props

  //Display modal
  const [currentModal, setCurrentModal] = useState<null | string>(null)

  //Hide if deleted
  const [isDeleted, setIsDeleted] = useState<boolean>(false)

  //Select message to delete and open confirmation modal
  function remove() {
    {
      store.dispatch(topicActivate(props.currentTopic))
      setCurrentModal(MODAL_DELETE)
    }
  }

  //Delete message in database
  //If ok remove from message list
  async function removeConfirm() {
    store.dispatch(messageStatus(STATUS_SAVING))
    const response: any = await store.dispatch(messageDestroy(props.currentMessage.aid))
    if (response.error) {
      store.dispatch(messageStatus(STATUS_SAVE_ERROR))
    } else {
      store.dispatch(
        topicEditQuestion(
          props.currentQuestion.id,
          "Messages",
          props.currentQuestion.Messages.filter((x) => x.aid !== props.currentMessage.aid)
        )
      )
      store.dispatch(
        topicEditQuestion(props.currentQuestion.id, "messagesCount", props.currentQuestion.messagesCount - 1)
      )
      /*if(props.onDelete){
        props.onDelete()
      }*/
      setIsDeleted(true)
      store.dispatch(messageStatus(STATUS_SAVED))
      setCurrentModal(null)
    }
  }

  return (
    <div
      className={"flex messages-card" + (props.currentMessage.color ? " message-exportable" : "")}
      id={props.id ? props.id : props.currentMessage.aid}>
      {currentModal === MODAL_DELETE && (
        <ModalConfirm
          onNo={() => setCurrentModal(null)}
          onYes={removeConfirm}
          status={props.message.status}
          text={t("message_ask_delete", {
            message: props.currentMessage.text
          })}
          textBold={t("utils_next_ask")}
        />
      )}

      <div className="message-user medgrey-t">
        <FontAwesomeIcon icon={faUserCircle} />
      </div>

      {isDeleted ? (
        <div className="flex1">
          <i>{t("message_deleted")}</i>
        </div>
      ) : (
        <div className="flex1 flex">
          <div className="flex1">
            <div className="message-text">
              {props.currentMessage.hasAlert && (
                <Tooltip text={t("message_flag_has_alert")}>
                  <span className="inline-block w-2.5 h-2.5 rounded-full orange mr-1.5 cursor-help" />
                </Tooltip>
              )}
              {props.search.length
                ? renderWithRedactions(props.currentMessage.text, [props.search])
                : renderWithRedactions(props.currentMessage.text)}
            </div>
          </div>

          <div className="width-20" />

          <div>
            <ColorPickerWidget
              title=" "
              isNullAllowed
              selectedColor={props.currentMessage.color}
              colors={props._session.accountColors.tags.map((tag: Tag) => tag.color)}
              onSelect={(color) => props.onSetColor(color)}
            />
            <div className="height-20" />
            <div className="flex">
              <Space />
              {props._session.userRole === "ADMIN" && <ListButton icon={faTrash} onClick={remove} />}
              <Space />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const mapStateToProps = (state) => ({
  _session: state._session,
  message: state.message,
  survey: state.survey
})

export default connect(mapStateToProps)(withTranslation()(DashboardQuestionOpenWidget))
